import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    List,
    ListItem,
    ListItemText,
    Divider,
    Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { fetchOpeningHoursForPeriod } from '../../../lib/apiClientOpeningHours';
import AddOpeningHoursDialog from './AddOpeningHoursDialog';
import DeleteOpeningHoursDialog from './DeleteOpeningHoursDialog';
import { useTranslation } from 'react-i18next';

const ShowOpeningHoursDialog = ({ open, onClose, businessId }) => {
    const { t } = useTranslation("showOpeningHoursDialog");

    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [list, setList] = useState([]);
    const [error, setError] = useState('');
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const buildDayList = (apiData) => {
        const result = [];
        const from = new Date(fromDate);
        const to = new Date(toDate);
        const EN_WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

        for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const dateISO = `${yyyy}-${mm}-${dd}`;
            const dateDisplay = `${dd}-${mm}-${yyyy}`;
            const weekdayRaw = EN_WEEKDAYS[d.getDay()];
            const weekdayDisplay = t("weekdayNames." + d.getDay());

            const match = apiData.find(item =>
                dateISO >= item.validFrom &&
                dateISO <= item.validTo &&
                item.weekDay === weekdayRaw
            );

            result.push({
                date: dateDisplay,
                weekday: weekdayDisplay,
                openTime: match?.openTime || null,
                closeTime: match?.closeTime || null
            });
        }

        return result;
    };

    const handleFetch = async () => {
        try {
            const data = await fetchOpeningHoursForPeriod(businessId, fromDate, toDate);
            console.log("Fetched opening hours data:", data);
            const dayList = buildDayList(data);
            setList(dayList);
            setError('');

        } catch (err) {
            if (err.message !== "No opening hours found in this period.") {
                setError(t("errorLoad"));
            }
            setList([]);
        }
    };

    const isViewRangeInvalid =
        fromDate && toDate && new Date(toDate) < new Date(fromDate);

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">

                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    {t("title")}
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>

                    <Box display="flex" gap={2} mb={2}>
                        <TextField
                            fullWidth
                            label={t("from")}
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                            fullWidth
                            label={t("to")}
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>

                    {isViewRangeInvalid && (
                        <Box color="red" mt={2} fontWeight="bold">
                            {t("dateRangeInvalid")}
                        </Box>
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleFetch}
                    >
                        {t("load")}
                    </Button>

                    {error && (
                        <Box mt={2} color="red" textAlign="center" fontWeight="bold">
                            {error}
                        </Box>
                    )}

                    <Button
                        variant="outlined"
                        color="secondary"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={() => setAddDialogOpen(true)}
                    >
                        {t("addNew")}
                    </Button>

                    <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={() => setDeleteDialogOpen(true)}
                    >
                        {t("delete")}
                    </Button>

                    <Divider sx={{ my: 2 }} />

                    <List>
                        {list.map((d, idx) => (
                            <ListItem key={idx}>
                                <ListItemText
                                    primary={`${d.weekday} (${d.date})`}
                                    secondary={
                                        d.openTime
                                            ? `${d.openTime} - ${d.closeTime}`
                                            : t("closed")
                                    }
                                />
                            </ListItem>
                        ))}

                        {list.length === 0 && !error && (
                            <Box textAlign="center" mt={2} color="gray">
                                {t("noData")}
                            </Box>
                        )}
                    </List>

                </DialogContent>
            </Dialog>

            <AddOpeningHoursDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                businessId={businessId}
                onSuccess={() => { }}
            />

            <DeleteOpeningHoursDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                businessId={businessId}
            />

        </>
    );
};

export default ShowOpeningHoursDialog;
